import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import type { PathItemObject } from '@jotta/types/Files'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { FileNavLink } from '../FileNavLink/FileNavLink'
import { fileRouteContextConfig } from '../FileRoute/fileRouteContextConfig'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'
import { FileApiPathNavLink } from '../FileRouteLink/FileApiPathLink'
import styles from './FileBreadCrumb.module.scss'
import { FileBreadCrumbEllipsis } from './FileBreadCrumbEllipsis'
import { FileBreadCrumbItem } from './FileBreadCrumbItem'
import { FileBreadCrumbNav } from './FileBreadCrumbNav'
import SvgCaretLeft from '@jotta/ui/SvgCaretLeft'
import SvgCaretRight from '@jotta/ui/SvgCaretRight'

const debug = Debug('jotta:files:FileBreadCrumb')
debug.enabled = false

export const FileBreadCrumb = observer<{
  items: readonly PathItemObject[]
  onContextMenuSelect?: (e: Event) => void
}>(function FileBreadCrumb({ items, onContextMenuSelect, ...props }) {
  const params = useFileRouteParams()
  const isRoot = !items.length
  const currentContext = params.context
  const currentContextConfig = fileRouteContextConfig[currentContext]
  const rootContext = currentContextConfig.rootContext || params.context
  const rootContextConfig = fileRouteContextConfig[rootContext]
  const context = rootContext
  const children = items.slice(-2)
  const ellipsis = items.slice(0, -2)
  const parentItem = items.at(-2)

  return (
    <nav data-testid="FileBreadCrumb" className={styles.breadcrumb} {...props}>
      {!isRoot && (
        <FileBreadCrumbItem hide="large">
          <FileApiPathNavLink
            end
            context={parentItem ? currentContext : rootContext}
            path={parentItem?.path || rootContextConfig.apiPath}
          >
            <PlainBrandIcon icon={SvgCaretLeft} className={styles.back} />
          </FileApiPathNavLink>
        </FileBreadCrumbItem>
      )}

      <FileBreadCrumbItem
        key={context}
        hide={!isRoot || context !== params.context ? 'small' : undefined}
        isRoot={isRoot}
      >
        <FileNavLink context={context} />
        {!isRoot && (
          <PlainBrandIcon icon={SvgCaretRight} className={styles.separator} />
        )}
      </FileBreadCrumbItem>

      <FileBreadCrumbEllipsis items={ellipsis} context={currentContext} />

      {children.map((item, i, arr) => {
        const isFirst = i === 0
        const isLast = i === arr.length - 1
        return (
          <FileBreadCrumbNav
            key={`breadcrumb-${item.path}${i}`}
            item={item}
            shrink
            trailingArrow={!isLast}
            params={params}
            onContextMenuSelect={isLast ? onContextMenuSelect : undefined}
            hide={isFirst && !isLast ? 'small' : undefined}
          />
        )
      })}
    </nav>
  )
})
